/**
 * PaymentPage - Clean Shopify-inspired payment selection
 * Route: /order/:sessionId/pay
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useBrandTheme } from "@/lib/brand-theme";
import { BrandLogo } from "@/components/BrandLogo";
import { formatCurrency, cn, getIsFreeItemForOrderItem } from "@/lib/utils";
import { Card } from "@/components/Card";
import { CartItem } from "@/components/CartItem";
import { Button } from "@/components/Button";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorMessage } from "@/components/ErrorMessage";
import {
  useOrder,
  useInitializePaystackTransaction,
} from "@/api/hooks";
import {
  useOrderDetailsStore,
} from "@/lib/order-details-store";
import { useTheme } from "@/lib/theme/ThemeProvider";
import { checkoutEntryPath, readMagentoCartIds, returnToExpressCart } from "@/lib/express-cart/device";
import {
  buildProductVerificationGroups,
  verifyProductGroups,
} from "@/lib/product-verification";
import {
  isMagentoCheckout,
  applyMagentoShippingTotals,
  createMagentoOrder
} from "@/lib/magento-helper";
import Paystack from "@paystack/inline-js";
import PaystackPop from "@paystack/inline-js";

// Secure checkout badge
function SecureBadge() {
  const { resolvedTheme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  return (
    <div className="flex items-center gap-3">
      <div className="secure-badge">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <span>Secure checkout</span>
      </div>

      <button
        onClick={toggleTheme}
        className="p-2 rounded-lg hover:bg-(--color-surface-secondary) transition-colors"
        aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
        title={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
      >
        {resolvedTheme === "dark" ? (
          <svg
            className="w-5 h-5 text-(--color-text-secondary)"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5 text-(--color-text-secondary)"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        )}
      </button>
    </div>
  );
}

// Breadcrumb component
function Breadcrumb() {
  return (
    <nav className="checkout-breadcrumb mb-6">
      <span className="checkout-breadcrumb-item completed">Cart</span>
      <svg
        className="w-4 h-4 text-(--color-text-placeholder)"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5l7 7-7 7"
        />
      </svg>
      <span className="checkout-breadcrumb-item completed">Shipping</span>
      <svg
        className="w-4 h-4 text-(--color-text-placeholder)"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5l7 7-7 7"
        />
      </svg>
      <span className="checkout-breadcrumb-item completed">Review</span>
      <svg
        className="w-4 h-4 text-(--color-text-placeholder)"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5l7 7-7 7"
        />
      </svg>
      <span className="checkout-breadcrumb-item active">Payment</span>
    </nav>
  );
}

export function PaymentPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const { theme } = useBrandTheme();
  const { resolvedTheme } = useTheme();

  const [showSummary, setShowSummary] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stockWarning, setStockWarning] = useState<string | null>(null);
  const [magentoOrdered, setMagentoOrdered] = useState(false);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<{
    id: string;
    name: string;
    description: string;
    price_cents: number;
  } | null>(null);
  const [paystackReady, setPaystackReady] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    "paystack" | "apple_pay"
  >("paystack");

  // Check if current brand should use live Paystack key
  const isLivePaystackBrand = theme?.is_live ?? false;

  // Fetch real order data from API
  const {
    data: orderData,
    isLoading: orderLoading,
    error: orderError,
  } = useOrder(orderId);
  const initializePaystackTransaction = useInitializePaystackTransaction();
  const queryClient = useQueryClient();

  // Load shipping method from localStorage
  useEffect(() => {
    const savedShippingMethod = localStorage.getItem("selectedShippingMethod");
    if (savedShippingMethod) {
      try {
        const shippingOption = JSON.parse(savedShippingMethod);
        setSelectedShippingMethod(shippingOption);
      } catch (error) {
        console.error(
          "Failed to parse shipping method from localStorage:",
          error,
        );
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(error, {
            tags: { component: "PaymentPage", action: "parseShippingMethod" },
          });
        }
      }
    }
  }, []);

  // Use persisted order details from store (same data as OrderPage order summary)
  const orderDetailsStore = useOrderDetailsStore();
  const storeCartItems = useMemo(
    () => orderDetailsStore.cartItemsByBrand.flatMap(([, items]) => items),
    [orderDetailsStore.cartItemsByBrand],
  );

  const calculatedSubtotalCents = storeCartItems.reduce(
    (sum, item) => sum + item.line_total_cents,
    0,
  );
  const calculatedShippingCents = orderDetailsStore.shippingCents;
  const calculatedDiscountCents =
    orderDetailsStore.totalAppliedDiscount +
    orderDetailsStore.automatedDiscountCents;
  const subtotalCents =
    orderDetailsStore.paymentSubtotalCents ?? calculatedSubtotalCents;
  const shippingCents =
    orderDetailsStore.paymentShippingCents ?? calculatedShippingCents;
  const discountTotal =
    orderDetailsStore.paymentDiscountCents ?? calculatedDiscountCents;
  // VAT (15%) applies to the items subtotal only - shipping and discounts are excluded
  const taxCents = orderDetailsStore.paymentTaxCents ?? Math.round(subtotalCents * 0.15);
  const totalCents =
    orderDetailsStore.paymentTotalCents ??
    subtotalCents + shippingCents - discountTotal + taxCents;
  const activeDiscountCode =
    [...new Set(orderDetailsStore.totalDiscountDetail.map((d) => d.code))].join(
      ",",
    ) || null;
  const hasDiscount = discountTotal > 0 || Boolean(activeDiscountCode);

  const noopUpdate = async () => {};
  const noopRemove = async () => {};

  // Redirect if no order ID
  useEffect(() => {
    if (!orderId && sessionId) {
      navigate(checkoutEntryPath(sessionId));
    }
  }, [orderId, sessionId, navigate]);

  // Paystack is now loaded via npm package - always ready
  useEffect(() => {
    setPaystackReady(true);
  }, []);

  // Confirm Magento shipping and authoritative totals on page load,
  // not on payment click. Non-Magento checkouts skip this entirely.
  useEffect(() => {
    let cancelled = false;
    const details = useOrderDetailsStore.getState();
    const store = {
      ...details,
      magentoCartIds: readMagentoCartIds(sessionId),
    };
    const shippingDetails = store.shippingDetails;
    if (!shippingDetails || !isMagentoCheckout(store)) return;

    applyMagentoShippingTotals(store, shippingDetails, {
      subtotalCents: calculatedSubtotalCents,
      shippingCents: calculatedShippingCents,
      discountCents: calculatedDiscountCents,
    })
      .then((totals) => {
        if (cancelled || !totals) return;
        useOrderDetailsStore.getState().setOrderDetails({
          ...useOrderDetailsStore.getState(),
          paymentSubtotalCents: totals.subtotalCents,
          paymentShippingCents: totals.shippingCents,
          paymentDiscountCents: totals.discountCents,
          paymentTaxCents: totals.taxCents,
          paymentTotalCents: totals.totalCents,
        });
      })
      .catch((error) => {
        console.error("[PaymentPage - magentoShipping] -", error);
        if (!cancelled) {
          setError(
            error instanceof Error
              ? error.message
              : "Failed to confirm Magento payment totals. Please try again.",
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [calculatedSubtotalCents, calculatedShippingCents, calculatedDiscountCents, sessionId]);

  const verifyStockBeforePayment = useCallback(async (
    store: ReturnType<typeof useOrderDetailsStore.getState>,
  ): Promise<boolean> => {
    const groups = buildProductVerificationGroups({
      isMarketplace: store.isMarketplace,
      cartItemsByBrand: store.cartItemsByBrand,
      marketplaceRetailers: store.marketplaceRetailers,
      apiConfig: store.apiConfig,
      retailerId: store.themeId,
    });

    if (groups.length === 0) return true;

    const result = await verifyProductGroups(groups);
    if (result.failedRetailerIds.length > 0) {
      setStockWarning(
        "We could not confirm stock for some items. You can continue with payment.",
      );
    }

    // Price mismatch (Cumi or Magento): refetch the cart and send the shopper
    // back so the refreshed price is shown before payment continues.
    if (result.priceIssues.length > 0) {
      const itemNames = result.priceIssues
        .map(({ item }) => item.title)
        .join(", ");
      if (sessionId) {
        void queryClient.refetchQueries({
          queryKey: ["cart", sessionId],
          exact: true,
        });
      }
      const priceError = `Price has changed for ${itemNames}. Please review the updated price before continuing.`;
      // Express cart sessions go back to the express cart, with the same message.
      if (returnToExpressCart(sessionId, priceError)) return false;
      navigate(`/order/${sessionId}`, {
        state: { stockError: priceError },
      });
      return false;
    }

    if (result.issues.length === 0) return true;

    const details = result.issues.map((issue) =>
      issue.type === "out_of_stock"
        ? `${issue.item.title} is out of stock`
        : `${issue.item.title} now has only ${issue.stock} available`,
    );
    const stockError = `Stock changed before payment: ${details.join("; ")}. Adjust your cart before continuing.`;
    // Express cart sessions go back to the express cart, with the same message.
    if (returnToExpressCart(sessionId, stockError)) return false;
    navigate(`/order/${sessionId}`, {
      state: { stockError },
    });
    return false;
  }, [navigate, queryClient, sessionId]);

  const handlePaystackPayment = useCallback(async () => {
    if (!sessionId || !orderData || !orderId) {
      setError("Order data is not available. Please try again.");
      return;
    }

    const details = useOrderDetailsStore.getState();
    const store = {
      ...details,
      magentoCartIds: readMagentoCartIds(sessionId),
    };
    const shippingDetails = store.shippingDetails;
    if (!shippingDetails) {
      setError("Shipping information is not available. Please try again.");
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);
      setStockWarning(null);

      if (!(await verifyStockBeforePayment(store))) {
        setIsProcessing(false);
        return;
      }

      if (isMagentoCheckout(store)) {
        const customerId = orderData.order.customer_id;
        const email = shippingDetails.email;

        if (!customerId || !email) {
          throw new Error("Magento customer information is incomplete. Please try again.");
        }

        if (!store.isMarketplace) {
          const magentoCartId = store.themeId
            ? store.magentoCartIds[store.themeId]
            : undefined;
          if (!magentoCartId || !store.themeId) {
            throw new Error("Magento cart information is incomplete. Please try again.");
          }

          await createMagentoOrder({
            cart_id: magentoCartId,
            retailer_id: store.themeId,
            email,
            customer_id: customerId,
            nile_order_id: orderId
          });
          setMagentoOrdered(true);
        } else {
          const magentoShipments = store.shipments.filter((shipment) =>
            store.marketplaceRetailers.some(
              (retailer) =>
                retailer.retailer_id === shipment.child_retailer_id &&
                retailer.integration_type === "magento",
            ),
          );

          for (const shipment of magentoShipments) {
            const magentoCartId = store.magentoCartIds[shipment.child_retailer_id];
            if (!magentoCartId) {
              throw new Error("Magento cart information is incomplete. Please try again.");
            }

            await createMagentoOrder({
              cart_id: magentoCartId,
              retailer_id: shipment.child_retailer_id,
              email,
              customer_id: customerId,
              nile_order_id: orderId
            });
          }
          setMagentoOrdered(true);
        }
      }

      const publicKey = isLivePaystackBrand
        ? import.meta.env.VITE_PAYSTACK_LIVE_PUBLIC_KEY
        : import.meta.env.VITE_PAYSTACK_PUBLIC_KEY;

      if (!publicKey) {
        setError("Payment configuration is missing. Please contact support.");
        setIsProcessing(false);
        return;
      }

      const metadata = {
        order_id: orderId,
        session_id: sessionId,
        shipping_cost: shippingCents,
        shipping_name: selectedShippingMethod?.name || null,
        discount_total_cents: discountTotal,
        external_cart_id: orderData.order.external_cart_id || null,
        // paystack_last4: body.last4 ?? null,
        paymet_provider: "paystack",
        payment_amount_cents: totalCents,
        custom_fields: [
          {
            display_name: "Order ID",
            variable_name: "order_id",
            value: orderId,
          },
          {
            display_name: "Session ID",
            variable_name: "session_id",
            value: sessionId || "",
          },
          {
            display_name: "Shipping Cost",
            variable_name: "shipping_cost",
            value: shippingCents || null,
          },
        ],
      };

      if (isLivePaystackBrand) {
        try {
          const accessCodeKey = `paystack_access_code_${orderId}`;
          let local_access_code = localStorage.getItem(accessCodeKey);

          if (!local_access_code) {
            const {access_code} = await initializePaystackTransaction.mutateAsync(metadata);

            local_access_code = access_code
            localStorage.setItem(accessCodeKey, access_code);
          }

          const popup = new PaystackPop();

          popup.resumeTransaction(local_access_code, {
            onSuccess: async (transaction) => {
              localStorage.removeItem(`paystack_access_code_${orderId}`);

              navigate(
                `/order/${sessionId}/success?order_id=${orderId}&reference=${transaction.reference}`,
              );
            },
            onCancel: () => {
              localStorage.removeItem(`paystack_access_code_${orderId}`);
              setIsProcessing(false);
            },
            onError: (error) => {
              console.error("resumeTransactionError -", error);
              localStorage.removeItem(`paystack_access_code_${orderId}`);
              setError(error.message || "Payment failed. Please try again.");
              setIsProcessing(false);
            },
          });
        } catch (error) {
          console.error("paystackInitializeError -", error);
          localStorage.removeItem(`paystack_access_code_${orderId}`);
          setError(
            error instanceof Error
              ? error.message
              : "Failed to initialize payment. Please try again.",
          );
          setIsProcessing(false);
        }

        return;
      }

      const paystack = new Paystack();

      paystack.newTransaction({
        key: publicKey,
        email: orderData.order.shipping_info?.email || "customer@example.com",
        amount: totalCents,
        currency: orderData.order.currency || "ZAR",
        reference: `nile-ps-${orderId.slice(0, 8)}`,
        firstName: orderData.order.shipping_info?.first_name.trim() || "",
        lastName: orderData.order.shipping_info?.last_name.trim() || "",
        phone: orderData.order.shipping_info?.phone || "",
        metadata: metadata,
        onSuccess: async (transaction) => {
          navigate(
            `/order/${sessionId}/success?order_id=${orderId}&reference=${transaction.reference}`,
          );
        },
        onCancel: () => {
          setIsProcessing(false);
        },
        onError: (error: { message: string }) => {
          console.error("Payment error:", error);
          setError(error.message || "Payment failed. Please try again.");
          setIsProcessing(false);
        },
      });
    } catch (error) {
      console.error("[PaymentPage - orderCreation] -", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to create order. Please try again.",
      );
      setIsProcessing(false);
    }
  }, [
    discountTotal,
    shippingCents,
    totalCents,
    initializePaystackTransaction,
    isLivePaystackBrand,
    navigate,
    orderData,
    orderId,
    selectedShippingMethod,
    sessionId,
    verifyStockBeforePayment,
  ]);

  const handleApplePayPayment = useCallback(async () => {
    if (!sessionId || !orderId || !orderData) {
      setError("Order data is not available. Please try again.");
      return;
    }

    const store = useOrderDetailsStore.getState();
    const shippingDetails = store.shippingDetails;
    if (!shippingDetails) {
      setError("Shipping information is not available. Please try again.");
      return;
    }

    setIsProcessing(true);
    setError(null);
    setStockWarning(null);

    if (!(await verifyStockBeforePayment(store))) {
      setIsProcessing(false);
      return;
    }

    const publicKey = isLivePaystackBrand
      ? import.meta.env.VITE_PAYSTACK_LIVE_PUBLIC_KEY
      : import.meta.env.VITE_PAYSTACK_PUBLIC_KEY;

    if (!publicKey) {
      setError("Payment configuration is missing. Please contact support.");
      setIsProcessing(false);
      return;
    }

    const popup = new PaystackPop();

    const metadata = {
      order_id: orderId,
      session_id: sessionId,
      shipping_cost: shippingCents,
      shipping_name: selectedShippingMethod?.name || null,
      discount_total_cents: discountTotal,
      external_cart_id: orderData.order.external_cart_id || null,
      custom_fields: [
        {
          display_name: "Order ID",
          variable_name: "order_id",
          value: orderId,
        },
        {
          display_name: "Session ID",
          variable_name: "session_id",
          value: sessionId || "",
        },
        {
          display_name: "Shipping Cost",
          variable_name: "shipping_cost",
          value: shippingCents || null,
        },
      ],
    };

    popup.checkout({
      key: publicKey,
      email: orderData.order.shipping_info?.email || "customer@example.com",
      amount: totalCents,
      currency: orderData.order.currency || "ZAR",
      reference: `nile-ps-${orderId.slice(0, 8)}`,
      firstName: orderData.order.shipping_info?.first_name.trim() || "",
      lastName: orderData.order.shipping_info?.last_name.trim() || "",
      phone: orderData.order.shipping_info?.phone || "",
      metadata: metadata,
      channels: ["apple_pay"],
      onSuccess: async (transaction: {
        reference: string;
        status: string;
        trans?: string;
        trxref?: string;
      }) => {
        navigate(
          `/order/${sessionId}/success?order_id=${orderId}&reference=${transaction.reference}`,
        );
      },
      onCancel: () => {
        setIsProcessing(false);
      },
      onError: (error: { message: string }) => {
        console.error("Apple Pay error:", error);
        setError(
          error.message || "Apple Pay payment failed. Please try again.",
        );
        setIsProcessing(false);
      },
    });
  }, [
    discountTotal,
    shippingCents,
    totalCents,
    isLivePaystackBrand,
    navigate,
    orderData,
    orderId,
    selectedShippingMethod?.name,
    sessionId,
    verifyStockBeforePayment,
  ]);

  // const handleCancel = () => {
  //   navigate(`/order/${sessionId}`);
  // };

  // Loading state
  if (orderLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--color-bg-primary)">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (orderError || !orderData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#f6f6f7]">
        <ErrorMessage
          title="Failed to load order"
          message={
            orderError instanceof Error ? orderError.message : "Order not found"
          }
          className="max-w-md"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-(--color-bg-primary) flex flex-col justify-between gap-5">
      {/* Header */}
      <div>
        <header className="bg-(--color-surface-primary) border-b border-(--color-surface-border-light)">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <BrandLogo brand={theme} />
              <SecureBadge />
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-8">
          <Breadcrumb />

          <div className="lg:grid lg:grid-cols-[1fr_380px] lg:gap-4">
            {/* Main content */}
            <div>
              <Card className="mb-6">
                <h2 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                  Payment method
                </h2>
                <p className="text-[14px] text-(--color-text-secondary) mb-4">
                  All transactions are secure and encrypted.
                </p>

                <div className="space-y-3">
                  {/* Paystack option */}
                  <button
                    type="button"
                    onClick={() => setSelectedPaymentMethod("paystack")}
                    className={cn(
                      "w-full flex items-center gap-3 p-4 border rounded-lg transition-colors text-left",
                      selectedPaymentMethod === "paystack"
                        ? "border-(--color-primary) bg-(--color-primary)/5-1 ring-(--color-primary)"
                        : "border-(--color-surface-border-light) bg-(--color-bg-secondary) hover:border-(--color-primary)/50",
                    )}
                  >
                    <div className="w-10 h-6 bg-[#00C3F7] rounded flex items-center justify-center shrink-0">
                      <span className="text-white text-[10px] font-bold">
                        PS
                      </span>
                    </div>
                    <div className="flex-1">
                      <span className="text-[14px] font-medium text-(--color-text-primary)">
                        Paystack
                      </span>
                      <p className="text-[12px] text-(--color-text-muted)">
                        Card, Bank Transfer, USSD, Mobile Money
                      </p>
                    </div>
                    <div
                      className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                        selectedPaymentMethod === "paystack"
                          ? "border-(--color-primary)"
                          : "border-(--color-text-muted)",
                      )}
                    >
                      {selectedPaymentMethod === "paystack" && (
                        <div className="w-2.5 h-2.5 rounded-full bg-(--color-primary)" />
                      )}
                    </div>
                  </button>

                  {/* Apple Pay option */}
                  <button
                    type="button"
                    onClick={() => setSelectedPaymentMethod("apple_pay")}
                    className={cn(
                      "w-full flex items-center gap-3 p-4 border rounded-lg transition-colors text-left",
                      selectedPaymentMethod === "apple_pay"
                        ? "border-(--color-primary) bg-(--color-primary)/5 ring-1 ring-(--color-primary)"
                        : "border-(--color-surface-border-light) bg-(--color-bg-secondary) hover:border-(--color-primary)/50",
                    )}
                  >
                    <div className="w-10 h-6 bg-black rounded flex items-center justify-center shrink-0 dark:bg-white">
                      <svg
                        viewBox="0 0 24 24"
                        className="w-5 h-4 text-white dark:text-black"
                        fill="currentColor"
                      >
                        <path d="M17.72 6.33c-.94-.11-1.86-.01-2.7.28-.39.14-.67.36-.83.67-.17.32-.2.69-.1 1.07.2.72.83 1.26 1.6 1.39.49.08.98.05 1.44-.1.3-.1.57-.28.74-.52.18-.26.27-.57.27-.9 0-.46-.16-.87-.47-1.2-.34-.36-.74-.6-1.22-.72l.27.03zM8.32 5.57c-.59-.02-1.13.16-1.56.54-.43.38-.7.86-.8 1.44-.08.5-.03.97.15 1.4.19.43.49.77.9 1.01.38.22.8.32 1.24.29.52-.04.98-.23 1.35-.56.37-.34.6-.75.66-1.24.06-.46 0-.9-.2-1.3-.2-.4-.49-.71-.87-.93-.3-.18-.63-.28-1-.3l.13.65zm8.4 13.86c-.2-.09-.43-.13-.68-.13-.4 0-.74.11-1.02.34-.27.22-.44.5-.5.84-.07.36-.02.69.14 1 .17.3.4.53.72.68.29.13.61.18.96.14.37-.04.69-.17.95-.39.27-.23.44-.51.5-.85.06-.33.02-.65-.13-.95-.14-.28-.36-.5-.65-.66l-.29.02zM5.57 19.3c-.21.14-.35.33-.42.57-.08.26-.05.51.08.75.13.23.32.4.58.5.24.09.5.1.77.03.27-.08.49-.23.65-.45.16-.23.22-.49.18-.78-.04-.26-.15-.48-.33-.65-.2-.18-.43-.27-.7-.27-.28 0-.53.1-.75.3h-.06zM22.28 7.58c-.3-1.36-1.06-2.5-2.17-3.3-1.1-.78-2.38-1.1-3.73-.93-.42.06-.82.17-1.2.33-.28.12-.54.27-.78.45l-.04.03c-.24-.16-.5-.3-.78-.41-.4-.15-.83-.23-1.27-.23-.5 0-.96.1-1.38.3-.3.14-.57.33-.8.56l-.05.05c-.27-.2-.57-.37-.9-.49-.48-.17-.99-.23-1.51-.17-.62.07-1.18.28-1.67.64-.5.37-.88.83-1.13 1.39-.26.57-.35 1.17-.28 1.8.07.6.27 1.15.6 1.63.32.46.73.82 1.22 1.07l.08.04c-.06.36-.06.73 0 1.1.08.45.24.87.49 1.24.26.39.58.71.96.96.4.27.84.44 1.32.52.38.06.76.05 1.13-.04l.1-.03c.16.16.35.3.56.42.34.19.72.3 1.12.34.35.03.69 0 1.02-.1.3-.09.57-.23.81-.42.24.15.51.26.8.33.38.09.77.12 1.17.08.52-.05 1-.2 1.42-.46.43-.27.78-.62 1.04-1.04.27-.44.43-.92.48-1.44.04-.44 0-.87-.12-1.28l.04-.02c.42-.28.77-.64 1.04-1.08.28-.46.45-.96.5-1.5.06-.58 0-1.14-.17-1.68l.05.02z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <span className="text-[14px] font-medium text-(--color-text-primary)">
                        Apple Pay
                      </span>
                      <p className="text-[12px] text-(--color-text-muted)">
                        Pay with Face ID, Touch ID, or passcode
                      </p>
                    </div>
                    <div
                      className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                        selectedPaymentMethod === "apple_pay"
                          ? "border-(--color-primary)"
                          : "border-(--color-text-muted)",
                      )}
                    >
                      {selectedPaymentMethod === "apple_pay" && (
                        <div className="w-2.5 h-2.5 rounded-full bg-(--color-primary)" />
                      )}
                    </div>
                  </button>
                </div>
              </Card>

              {/* {prunedItems.length > 0 && (
                <div className="warning-box mb-6">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div>
                    <p className="font-medium">
                      Some items are no longer available
                    </p>
                    <p className="text-[12px] opacity-80 mt-0.5">
                      The following items were removed from your cart and will
                      not be included in this order:
                    </p>
                    <ul className="text-[12px] opacity-80 mt-1 list-disc list-inside">
                      {prunedItems.map((item) => (
                        <li key={item.cart_id}>
                          {item.title ?? "Unknown item"}
                          {item.selected_variant
                            ? ` (${item.selected_variant})`
                            : ""}{" "}
                          ×{item.quantity}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )} */}

              {error && (
                <div className="error-box mb-6">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div>
                    <p className="font-medium">Payment Error</p>
                    <p className="text-[12px] opacity-80 mt-0.5">{error}</p>
                  </div>
                </div>
              )}
              {stockWarning && (
                <div className="warning-box mb-6">
                  <p className="text-[12px]">{stockWarning}</p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => sessionId && navigate(checkoutEntryPath(sessionId))}
                  variant="accent"
                  disabled={isProcessing || magentoOrdered}
                  className="opacity-100"
                >
                  Return to Review
                </Button>
                <Button
                  onClick={
                    selectedPaymentMethod === "apple_pay"
                      ? handleApplePayPayment
                      : handlePaystackPayment
                  }
                  disabled={isProcessing || !paystackReady}
                  isLoading={isProcessing}
                  fullWidth
                  size="lg"
                >
                  {isProcessing
                    ? "Processing..."
                    : !paystackReady
                      ? "Loading Payment Provider..."
                      : selectedPaymentMethod === "apple_pay"
                        ? `Apple Pay ${formatCurrency(totalCents)}`
                        : formatCurrency(totalCents)}
                </Button>
              </div>

              {/* Security Notice */}
              <div className="mt-6 flex items-center justify-center gap-2 text-[12px] text-(--color-text-muted)">
                <svg
                  className="w-4 h-4 text-(--color-success)"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                Your payment info is secure and encrypted
              </div>
            </div>

            {/* Order summary sidebar */}
            <div className="hidden lg:block">
              <div className="sticky top-8">
                <Card>
                  <h2 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                    Order summary
                  </h2>

                  {/* Products */}
                  <div className="border-b border-(--color-surface-border-light) pb-4 mb-4">
                    {storeCartItems.map((item) => (
                      <CartItem
                        key={item.id}
                        item={item}
                        onUpdateQuantity={async () => {}} // intentional
                        onRemove={async () => {}}
                        isUpdating={false}
                        isMarketplace={false}
                        isFreeItem={getIsFreeItemForOrderItem({
                          id: item.id,
                          product_id: item.product_id,
                          sku: item.sku,
                          quantity: item.quantity,
                          line_total_cents: item.line_total_cents,
                          unit_price_cents: item.unit_price_cents,
                        })}
                        compact
                      />
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="space-y-2 text-[14px]">
                    <div className="flex justify-between">
                      <span className="text-(--color-text-secondary)">
                        Subtotal
                      </span>
                      <span className="text-(--color-text-primary) font-medium tabular-nums">
                        {formatCurrency(subtotalCents)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-(--color-text-secondary)">
                        Shipping
                      </span>
                      <span className="text-(--color-text-primary) font-medium tabular-nums">
                        {shippingCents > 0
                          ? formatCurrency(shippingCents)
                          : "—"}
                      </span>
                    </div>
                    {selectedShippingMethod && shippingCents > 0 && (
                      <div className="flex justify-between -mt-1">
                        <p className="text-[12px] text-(--color-text-muted)">
                          {selectedShippingMethod.name}
                        </p>
                      </div>
                    )}
                    {hasDiscount && (
                      <div className="mt-3">
                        <div className="flex justify-between items-baseline">
                          <span className="text-(--color-text-secondary)">
                            Total discounts
                          </span>
                          <span className="text-(--color-success) font-medium tabular-nums">
                            - {formatCurrency(discountTotal)}
                          </span>
                        </div>
                      </div>
                    )}
                    {taxCents > 0 && (
                      <div className="flex justify-between">
                        <span className="text-(--color-text-secondary)">Tax</span>
                        <span className="text-(--color-text-primary) font-medium tabular-nums">
                          {formatCurrency(taxCents)}
                        </span>
                      </div>
                    )}
                    <div className="divider" />
                    <div className="flex justify-between items-baseline">
                      <span className="text-(--color-text-primary) font-semibold">
                        Total
                      </span>
                      <span className="price-total tabular-nums">
                        {formatCurrency(totalCents)}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Mobile order summary toggle */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-(--color-surface-primary) border-t border-(--color-surface-border-light) p-4 z-50">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setShowSummary(!showSummary)}
            className="flex items-center gap-2 text-[14px] text-(--color-secondary)"
          >
            <svg
              className={cn(
                "w-4 h-4 transition-transform",
                showSummary && "rotate-180",
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
            {showSummary ? "Hide" : "Show"} order summary
          </button>
          <span className="price-total tabular-nums">
            {formatCurrency(totalCents)}
          </span>
        </div>

        {showSummary && (
          <div className="mb-4 max-h-60 overflow-y-auto border-t border-(--color-surface-border-light) pt-4">
            {storeCartItems.map((item) => (
              <CartItem
                key={item.id}
                item={item}
                onUpdateQuantity={noopUpdate}
                onRemove={noopRemove}
                isUpdating={false}
                isMarketplace={false}
                isFreeItem={getIsFreeItemForOrderItem({
                  product_id: item.product_id,
                  sku: item.sku,
                  quantity: item.quantity,
                  line_total_cents: item.line_total_cents,
                  unit_price_cents: item.unit_price_cents,
                })}
                compact
              />
            ))}
            <div className="divider" />
            <div className="space-y-2 text-[14px]">
              <div className="flex justify-between">
                <span className="text-(--color-text-secondary)">Subtotal</span>
                <span className="tabular-nums">
                  {formatCurrency(subtotalCents)}
                </span>
              </div>
              {selectedShippingMethod && shippingCents > 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-(--color-text-secondary)">
                      Shipping
                    </span>
                    <span className="tabular-nums">
                      {formatCurrency(shippingCents)}
                    </span>
                  </div>
                  <div className="flex justify-between -mt-1">
                    <p className="text-[12px] text-(--color-text-muted)">
                      {selectedShippingMethod.name}
                    </p>
                  </div>
                </>
              )}
              {hasDiscount && (
                <div className="mt-3">
                  <div className="flex justify-between items-baseline">
                    <span className="text-(--color-text-secondary)">
                      Total discounts
                    </span>
                    <span className="text-(--color-success) font-medium tabular-nums">
                      - {formatCurrency(discountTotal)}
                    </span>
                  </div>
                </div>
              )}
              {taxCents > 0 && (
                <div className="flex justify-between">
                  <span className="text-(--color-text-secondary)">Tax</span>
                  <span className="tabular-nums">{formatCurrency(taxCents)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="border-t border-(--color-surface-border-light) pt-4 px-4 flex items-center justify-center gap-1.5 text-xs text-(--color-text-muted)">
          <span>Powered by</span>
          {resolvedTheme === "light" ? (
            <img
              src="/images/nile-wordmark-dark.png"
              alt="Nile"
              className="h-3.5"
            />
          ) : (
            <img
              src="/images/nile-wordmark-light.png"
              alt="Nile"
              className="h-3.5"
            />
          )}
        </div>
      </div>

      {/* Spacer for mobile sticky footer */}
      <div className="lg:hidden h-32" />

      <footer className="hidden lg:flex border-t border-(--color-surface-border-light) p-4 z-50 items-center justify-center gap-1.5 text-xs text-(--color-text-muted)">
        <span>Powered by</span>
        {resolvedTheme === "light" ? (
          <img
            src="/images/nile-wordmark-dark.png"
            alt="Nile"
            className="h-3.5"
          />
        ) : (
          <img
            src="/images/nile-wordmark-light.png"
            alt="Nile"
            className="h-3.5"
          />
        )}
      </footer>
    </div>
  );
}
